import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { repositoryApi } from '@/lib/api/repository';
import { analytics, EVENTS } from '@/lib/analytics';
import { getFeedbackSessionId } from '@/lib/feedback';
import { toast } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import type { FeedbackReasonCode, FeedbackSentiment } from '@/types/marketing';
import { logError } from '@/lib/logger';

const SENTIMENT_OPTIONS: { value: FeedbackSentiment; label: string }[] = [
  { value: 'blocked', label: 'Blocked' },
  { value: 'confusing', label: 'Confusing' },
  { value: 'fine', label: 'Works fine' },
];

const REASON_OPTIONS: Record<FeedbackSentiment, { value: FeedbackReasonCode; label: string }[]> = {
  blocked: [
    { value: 'permissions', label: 'Permissions or access' },
    { value: 'no_org', label: 'No org found' },
    { value: 'repo_choice', label: 'Not sure which repos' },
    { value: 'no_pr_workflow', label: 'No PR workflow' },
    { value: 'sync_delay', label: 'Sync is too slow' },
    { value: 'data_missing', label: 'Data looks missing' },
    { value: 'other', label: 'Other' },
  ],
  confusing: [
    { value: 'repo_choice', label: 'Not sure which repos' },
    { value: 'no_pr_workflow', label: 'No PR workflow' },
    { value: 'data_missing', label: 'Data looks missing' },
    { value: 'other', label: 'Other' },
  ],
  fine: [
    { value: 'other', label: 'Other' },
  ],
};

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  surface: string;
  step?: string;
  organizationId?: string;
  defaultSentiment?: FeedbackSentiment;
  defaultReasonCode?: FeedbackReasonCode;
  title?: string;
  context?: Record<string, unknown> | (() => Record<string, unknown>);
}

export default function FeedbackModal({
  isOpen,
  onClose,
  onSubmitted,
  surface,
  step,
  organizationId,
  defaultSentiment,
  defaultReasonCode,
  title = 'Report an obstacle',
  context,
}: FeedbackModalProps) {
  const { user } = useAuthStore();
  const [sentiment, setSentiment] = useState<FeedbackSentiment>(defaultSentiment || 'blocked');
  const [reasonCode, setReasonCode] = useState<FeedbackReasonCode | ''>(defaultReasonCode || '');
  const [message, setMessage] = useState('');
  const [requestHelp, setRequestHelp] = useState((defaultSentiment || 'blocked') === 'blocked');
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const reasonOptions = useMemo(() => REASON_OPTIONS[sentiment], [sentiment]);

  useEffect(() => {
    if (!isOpen) return;
    setSentiment(defaultSentiment || 'blocked');
    setReasonCode(defaultReasonCode || '');
    setMessage('');
    setRequestHelp((defaultSentiment || 'blocked') === 'blocked');
    setContactEmail(user?.email || '');
    setHasSubmitted(false);
    analytics.track({
      name: EVENTS.FEEDBACK_PROMPT_SHOWN,
      params: { surface, step, default_sentiment: defaultSentiment },
    });
  }, [isOpen, surface, step, defaultSentiment, defaultReasonCode, user?.email]);

  useEffect(() => {
    if (sentiment === 'blocked') {
      setRequestHelp(true);
    }
    if (sentiment === 'fine') {
      setRequestHelp(false);
      setReasonCode('');
    }
  }, [sentiment]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (!hasSubmitted) {
      analytics.track({ name: EVENTS.FEEDBACK_DISMISSED, params: { surface, step } });
    }
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        organization_id: organizationId,
        session_id: getFeedbackSessionId(),
        surface,
        step,
        sentiment,
        reason_code: reasonCode || undefined,
        message: message.trim() || undefined,
        request_help: requestHelp,
        contact_email: requestHelp && contactEmail.trim() ? contactEmail.trim() : undefined,
        page_path: window.location.pathname,
        context: typeof context === 'function' ? context() : context,
      };

      const response = await repositoryApi.submitFeedback(payload);

      analytics.track({
        name: EVENTS.FEEDBACK_SUBMITTED,
        params: {
          surface,
          step,
          sentiment,
          reason_code: reasonCode || undefined,
          request_help: requestHelp,
        },
      });

      if (requestHelp) {
        analytics.track({
          name: EVENTS.FEEDBACK_REQUESTED_HELP,
          params: { surface, step, sentiment },
        });
      }

      setHasSubmitted(true);
      if (response.email_sent) {
        toast.success('Thanks! Check your email for confirmation.');
      } else {
        toast.success('Thanks for the feedback!');
      }
      onSubmitted?.();
      onClose();
    } catch (error) {
      logError('Failed to submit feedback:', error);
      toast.error('Could not send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[51] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Help us remove blockers fast.</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              How did this step feel?
            </legend>
            <div className="flex flex-wrap gap-2">
              {SENTIMENT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSentiment(option.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                    sentiment === option.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                      : 'border-gray-200 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          {sentiment !== 'fine' && (
            <div>
              <label
                htmlFor="feedback-reason"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
              >
                What best describes the issue?
              </label>
              <select
                id="feedback-reason"
                value={reasonCode}
                onChange={(event) => setReasonCode(event.target.value as FeedbackReasonCode)}
                className="input w-full"
              >
                <option value="">Select a reason</option>
                {reasonOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {sentiment !== 'fine' && (
            <div>
              <label
                htmlFor="feedback-message"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
              >
                Optional details
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className="input min-h-[90px]"
                placeholder="What happened?"
              />
            </div>
          )}

          {sentiment !== 'fine' && (
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={requestHelp}
                onChange={(event) => setRequestHelp(event.target.checked)}
                className="rounded border-gray-300"
              />
              Request help from the team
            </label>
          )}

          {sentiment !== 'fine' && requestHelp && (
            <div>
              <label
                htmlFor="feedback-contact-email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
              >
                Where should we reply?
              </label>
              <input
                id="feedback-contact-email"
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                className="input w-full"
                placeholder="your@email.com"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Optional. We&apos;ll use this to respond to your feedback.
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={handleClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (sentiment !== 'fine' && !reasonCode)}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending...' : 'Send feedback'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}